import Client from "./ConsentClient";

export type ConsentHelperParams = {
  formId?: string;
  needsConsent?: boolean;
  isAllowSetCustomLocale?: boolean;
  selectedLocale?: string;
  forceShowConsentMessaging?: boolean;
  gdprId?: string;
  consentLabelEUCAN?: string;
  consentLabel?: string;
  privacyPolicy?: string;
  site?: string;
  source?: string;
  subscriptionKey: string;
  eapTermsOfService?: string;
  consentKey?: string;
};

export interface ConsentHelperForm extends HTMLElement {
  jquery?: boolean;
  0: ConsentHelperForm; // TODO: Factor this out
}

class ConsentHelper extends Client {
  params: ConsentHelperParams;
  form: ConsentHelperForm;
  submitButtonWrapper?: ParentNode | null;
  configPromise: Promise<ConsentHelper>;

  constructor(params: ConsentHelperParams, form: ConsentHelperForm) {
    super();
    this.params = params;
    const isJquery = !!(form instanceof jQuery || (form && form.jquery));
    this.form = isJquery ? form[0] : form;
    this.submitButtonWrapper =
      this.form.querySelector('[type="submit"]')?.parentNode;

    // A promise is expected for some legacy GDPR logic
    this.configPromise = this.init();

    return this;
  }

  async init() {
    const {
      needsConsent,
      isAllowSetCustomLocale,
      selectedLocale,
      forceShowConsentMessaging,
    } = this.params;

    try {
      await this.requestConfig();

      if (needsConsent) {
        if (isAllowSetCustomLocale && selectedLocale) {
          this.config.locale = selectedLocale;
        }
        this.insertConsent(forceShowConsentMessaging);
      }
    } catch (error) {
      if (needsConsent) {
        this.config = {
          locale: "",
          localeRequiresMarketingCommunicationOptIn: true,
        };

        this.insertConsent();
      }
    }

    return this;
  }

  buildConsentMarkup(requiresOptIn: boolean) {
    const { gdprId, consentLabelEUCAN, consentLabel, privacyPolicy } =
      this.params;

    const wrapperDiv = document.createElement("div");

    wrapperDiv.className = `gdpr-checkbox${requiresOptIn ? " gdpr-checkbox--optin" : ""
      }`;
    wrapperDiv.innerHTML = `
    <div class="gdpr-privacy-policy">${privacyPolicy}</div>
      <input type="checkbox" id="${gdprId}" name="${gdprId}"${requiresOptIn ? "" : " checked"
      } />
    <label>${consentLabelEUCAN && requiresOptIn
        ? consentLabelEUCAN
        : !requiresOptIn
          ? ""
          : consentLabel
      }</label>`;
    return wrapperDiv;
  }

  insertConsent(forceShowConsent = false) {
    const markup = this.buildConsentMarkup(
      !!(forceShowConsent || this.config.localeRequiresMarketingCommunicationOptIn)
    );
    if (this.submitButtonWrapper && this.submitButtonWrapper.parentNode) {
      this.submitButtonWrapper.parentNode.insertBefore(
        markup,
        this.submitButtonWrapper
      );
    } else {
      this.form.appendChild(markup);
    }
  }

  getConsentCheckboxValue() {
    const checkbox = <HTMLInputElement>this.form.querySelector(`#${this.params.gdprId}`);
    return checkbox ? checkbox.checked : true;
  }

  async submitForm(onSuccess: () => void, onFail: (error: unknown) => void) {
    try {
      if (!this.params.formId) {
        throw 'formId cannot be undefined or null'
      }

      const email = (<HTMLInputElement>this.form.querySelector('input[type="email"]'))?.value || '';

      await this.updatePreferences({
        paramsLocale: this.params.paramsLocale,
        formId: this.params.formId,
        site: this.params.site,
        source: this.params.source,
        email,
        formUrl: window.location.href,
        consentLabelText: this.params.consentLabel?.replace(/"/g, '\\"'),
        privacyPolicyText: this.params.privacyPolicy?.replace(/"/g, '\\"'),
        consentCheckboxValue: this.getConsentCheckboxValue(),
        ndaText: document.getElementById('nda-text')?.innerText.replace(/"/g, '\\"')
      });
      onSuccess();
    } catch (error: unknown) {
      console.error(error);
      onFail(error);
    }
  }
}

export default ConsentHelper;
